import type { ConversationPartApiResponse } from "@f2e/ui/chat-v2"

import type { ThreadSummaryResponse } from "@/hooks/api"
import { ClientChannelType } from "@/types"

export type ThreadOrderBy = "asc" | "desc"
export type ThreadStatusFilter = "all" | "pending"

export const THREAD_PAGE_LIMIT = 10
export const DEFAULT_THREAD_ORDER: ThreadOrderBy = "desc"
const CHANNEL_SOURCE_FILTERS = new Set<string>(Object.values(ClientChannelType))

export interface ThreadListPageSnapshot {
  items: ThreadSummaryResponse[]
  totalPages: number
  totalRecords: number
}

export interface ThreadListPageStructureDiff {
  addedIds: string[]
  removedIds: string[]
  orderChanged: boolean
}

export interface ThreadSummaryVersion {
  summary: Partial<ThreadSummaryResponse>
  fields: Partial<Record<keyof ThreadSummaryResponse, number>>
}

// Request revisions are shared by list and detail. A local mutation can update
// one field without discarding unrelated fields from an overlapping request.
export const mergeThreadSummaryVersion = (
  current: ThreadSummaryVersion | undefined,
  patch: Partial<ThreadSummaryResponse>,
  revision: number
): ThreadSummaryVersion => {
  const summary = { ...current?.summary }
  const fields = { ...current?.fields }
  for (const key of Object.keys(patch) as (keyof ThreadSummaryResponse)[]) {
    if (revision < (fields[key] ?? -1)) continue
    Object.assign(summary, { [key]: patch[key] })
    fields[key] = revision
  }
  return { summary, fields }
}

const stableSerialize = (value: unknown): string => {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "undefined"
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableSerialize).join(",")}]`
  }

  const entries = Object.entries(value as Record<string, unknown>).sort(
    ([left], [right]) => left.localeCompare(right)
  )
  return `{${entries
    .map(([key, entry]) => `${JSON.stringify(key)}:${stableSerialize(entry)}`)
    .join(",")}}`
}

export const createThreadListPageSnapshot = ({
  items,
  totalPages,
  totalRecords,
}: ThreadListPageSnapshot): ThreadListPageSnapshot => ({
  items,
  totalPages: Math.max(1, totalPages),
  totalRecords: Math.max(0, totalRecords),
})

export const getThreadListPageSnapshotKey = (
  snapshot: ThreadListPageSnapshot
) => stableSerialize(snapshot)

export const getThreadListPageStructureDiff = (
  current: ThreadListPageSnapshot,
  incoming: ThreadListPageSnapshot
): ThreadListPageStructureDiff => {
  const currentIds = current.items.map((thread) => thread.id)
  const incomingIds = incoming.items.map((thread) => thread.id)
  const currentIdSet = new Set(currentIds)
  const incomingIdSet = new Set(incomingIds)
  const sharedCurrentIds = currentIds.filter((id) => incomingIdSet.has(id))
  const sharedIncomingIds = incomingIds.filter((id) => currentIdSet.has(id))

  return {
    addedIds: incomingIds.filter((id) => !currentIdSet.has(id)),
    removedIds: currentIds.filter((id) => !incomingIdSet.has(id)),
    orderChanged:
      stableSerialize(sharedCurrentIds) !== stableSerialize(sharedIncomingIds),
  }
}

export const mergeThreadListPageSnapshotRows = (
  current: ThreadListPageSnapshot,
  incoming: ThreadListPageSnapshot
): ThreadListPageSnapshot => {
  const incomingById = new Map(
    incoming.items.map((thread) => [thread.id, thread])
  )

  return {
    ...current,
    items: current.items.map((thread) => incomingById.get(thread.id) ?? thread),
  }
}

export const patchThreadListPageSnapshot = (
  snapshot: ThreadListPageSnapshot,
  threadKey: string,
  patch: Partial<ThreadSummaryResponse>
): ThreadListPageSnapshot => ({
  ...snapshot,
  items: snapshot.items.map((thread) =>
    thread.id === threadKey ? { ...thread, ...patch } : thread
  ),
})

export const getSelectedThreadKey = (searchParams: URLSearchParams) => {
  const value = searchParams.get("tid")?.trim()
  return value || null
}

export const getAnchorHistoryId = (searchParams: URLSearchParams) => {
  const value = searchParams.get("anchor_history_id")
  if (!value) return null
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export const getLegacyHistoryId = (searchParams: URLSearchParams) => {
  const value = searchParams.get("hid")
  if (!value) return null
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export const canonicalizeHistorySelection = (
  searchParams: URLSearchParams,
  threadKey: string,
  historyId: number
) => {
  const params = new URLSearchParams(searchParams.toString())
  params.set("tid", threadKey)
  params.set("anchor_history_id", historyId.toString())
  params.delete("hid")
  return params
}

export const selectThread = (
  searchParams: URLSearchParams,
  threadKey: string
) => {
  const params = new URLSearchParams(searchParams.toString())
  params.set("tid", threadKey)
  params.delete("hid")
  params.delete("anchor_history_id")
  return params
}

export const clearThreadSelection = (searchParams: URLSearchParams) => {
  const params = new URLSearchParams(searchParams.toString())
  params.delete("tid")
  params.delete("hid")
  params.delete("anchor_history_id")
  return params
}

export const syncSelectedThreadParams = ({
  searchParams,
  threads,
  isMobile,
  resolvingHistory,
}: {
  searchParams: URLSearchParams
  threads?: ThreadSummaryResponse[]
  isMobile: boolean
  resolvingHistory: boolean
}) => {
  if (
    isMobile ||
    resolvingHistory ||
    threads === undefined ||
    getSelectedThreadKey(searchParams) ||
    getLegacyHistoryId(searchParams)
  ) {
    return null
  }

  const firstThread = threads[0]
  return firstThread ? selectThread(searchParams, firstThread.id) : null
}

export const buildThreadListSearchParams = ({
  page,
  agentId,
  externalUserId,
  clientUserId,
  customerSearch,
  orderBy,
  feedbackFilter,
  humanModeFilter,
  sourceFilter,
  statusFilter,
}: {
  page: number
  agentId: string
  externalUserId: string
  clientUserId: string
  customerSearch?: string
  orderBy: ThreadOrderBy
  feedbackFilter: string
  humanModeFilter: string
  sourceFilter: string
  statusFilter: ThreadStatusFilter
}) => {
  const params = new URLSearchParams({
    limit: THREAD_PAGE_LIMIT.toString(),
    offset: ((page - 1) * THREAD_PAGE_LIMIT).toString(),
  })
  if (agentId && agentId !== "none") {
    params.set("agent_id", agentId)
  }
  if (clientUserId) params.set("client_user_id", clientUserId)
  else if (externalUserId) params.set("external_user_id", externalUserId)
  if (customerSearch?.trim())
    params.set("customer_search", customerSearch.trim())
  if (orderBy !== DEFAULT_THREAD_ORDER) params.set("order_by", orderBy)
  if (feedbackFilter && feedbackFilter !== "no_filter") {
    params.set("feedback_filter", feedbackFilter)
  }
  if (humanModeFilter && humanModeFilter !== "no_filter") {
    params.set("human_mode", humanModeFilter)
  }
  if (sourceFilter && sourceFilter !== "all") {
    if (CHANNEL_SOURCE_FILTERS.has(sourceFilter)) {
      params.set("channel_type", sourceFilter)
    } else {
      params.set("source_type", sourceFilter)
    }
  }
  if (statusFilter === "pending") params.set("status", "pending")
  return params
}

// Thread API values are JSON data. Compare all fields, including metadata and
// attachments, without depending on property order or an updated_at contract.
export const equalThreadData = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true
  if (!left || !right || typeof left !== "object" || typeof right !== "object")
    return false
  if (Array.isArray(left) !== Array.isArray(right)) return false
  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const keys = Object.keys(leftRecord)
  return (
    keys.length === Object.keys(rightRecord).length &&
    keys.every(
      (key) =>
        Object.prototype.hasOwnProperty.call(rightRecord, key) &&
        equalThreadData(leftRecord[key], rightRecord[key])
    )
  )
}

export const mergeThreadParts = (
  current: ConversationPartApiResponse[],
  incoming: ConversationPartApiResponse[]
) => {
  const keyed = new Map<string, ConversationPartApiResponse>()
  for (const part of current) {
    const key =
      part.id == null
        ? `${part.history_id}:${part.conversation_group_id}:${part.sequence}`
        : `id:${part.id}`
    keyed.set(key, part)
  }
  let changed = false
  for (const part of incoming) {
    const key =
      part.id == null
        ? `${part.history_id}:${part.conversation_group_id}:${part.sequence}`
        : `id:${part.id}`
    if (equalThreadData(keyed.get(key), part)) continue
    keyed.set(key, part)
    changed = true
  }
  if (!changed) return current
  return [...keyed.values()].sort((left, right) => {
    const createdComparison = (left.created_at || "").localeCompare(
      right.created_at || ""
    )
    if (createdComparison !== 0) return createdComparison
    return (left.id ?? left.sequence) - (right.id ?? right.sequence)
  })
}
